<template>
    <div class="flex flex-col h-full w-full gap-2 grid-border">
        <div class="flex flex-col h-0 grow w-full">
            <dx-data-grid
                ref="dxGridRef"
                column-resizing-mode="nextColumn"
                :selected-row-keys="preselectedRows"
                :toolbar="{ items: toolbarItems }"
                :key-expr="meta?.keyExpr"
                :columns="meta?.columns"
                :data-source="displayList"
                :focused-row-enabled="true"
                :allow-column-resizing="true"
                :allow-column-reordering="true"
                :column-auto-width="true"
                :column-min-width="70"
                :auto-navigate-to-focused-row="false"
                :hover-state-enabled="true"
                :show-column-lines="true"
                :show-row-lines="true"
                :show-column-headers="showColumnHeaders"
                :word-wrap-enabled="meta?.wordWrapEnabled"
                :column-chooser="{ enabled: true, mode: 'select' }"
                :filter-panel="{ visible: showFilterPanel }"
                :filter-row="{ visible: true }"
                :fixing="{ enabled: true }"
                :load-panel="{ enabled: true }"
                :scrolling="{ mode: meta?.scrollingMode || 'standart' }"
                :paging="{ enabled: meta?.isPagingEnabled || false }"
                :search-panel="{ visible: true, placeholder: 'Фильтр...', locateInMenu: 'auto' }"
                :export="{ enabled: true, allowExportSelectedData: true, formats: exportFormats }"
                :state-storing="{ enabled: true, type: 'custom', customSave: saveState, savingTimeout: 500 }"
                :selection="{
                    mode: meta?.selectionMode || 'single',
                    showCheckBoxesMode: meta?.showCheckBoxesMode || 'always',
                    allowSelectAll: meta?.allowSelectAll || false,
                }"
                @focused-row-changed="onRoFocusChanged"
                @selection-changed="selectionChanged"
                @saved="onSaved"
                @content-ready="onContentReady"
                @exporting="onExporting"
                @row-click="onRowClick"
                @editor-preparing="onEditorPreparing"
            >
                <dx-toolbar :visible="props.showToolbar" :height="'64px'">
                    <dx-item
                        v-for="(item, index) in toolbarConfigAfter"
                        :key="index"
                        location="after"
                        :name="item.component.name"
                    >
                        <dx-button
                            v-if="item.widget === 'dx-button'"
                            class="ai-button-base"
                            :hint="item.component.hint"
                            :disabled="refs[item.component.disabledRefName]"
                            @click="
                                event => {
                                    event['item'] = item;
                                    event['eventName'] = 'click';
                                    eventController(event);
                                }
                            "
                        >
                            <component :is="item.component.htmlElement" />
                        </dx-button>
                        <dx-text-box
                            v-if="item.widget === 'dx-text-box'"
                            class="ai-toolbar-texteditor"
                            :label="
                                refs[item.component.valueLabelName]
                                    ? refs[item.component.valueLabelName]
                                    : item.component.valueLabelName
                            "
                            @enter-key="
                                event => {
                                    event['item'] = item;
                                    event['eventName'] = 'enter-key';
                                    eventController(event);
                                }
                            "
                            @key-up="
                                event => {
                                    event['item'] = item;
                                    event['eventName'] = 'key-up';
                                    eventController(event);
                                }
                            "
                            v-model:value="refs[item.component.valueRefName.name]"
                            :width="item.component.width"
                        >
                            <DxTextBoxButton
                                v-for="(inputButton, indexButton) in item.component.inputButtons"
                                :key="indexButton"
                                :name="inputButton.name"
                                :options="inputButton.options"
                                location="after"
                            />
                        </dx-text-box>
                        <DxDropDownButton
                            v-if="item.widget === 'dx-drop-down-button'"
                            :split-button="true"
                            :icon="item.component.htmlElement"
                            hint="Экспортировать"
                            :items="item.component.items"
                            :dropDownOptions="item.component.dropDownOptions"
                            @initialized="onDxDropDownButtonInitialized"
                        >
                            <dx-item v-for="(item, index) in item.component.items" :key="index">
                                <div
                                    class="ai-dx-drop-down-button-item"
                                    @click="
                                        event => {
                                            event['item'] = item;
                                            event['eventName'] = 'click';
                                            eventController(event);
                                        }
                                    "
                                >
                                    <component
                                        class="ai-dx-drop-down-button-item-svg"
                                        :is="item.component.htmlElement"
                                    />
                                    <div>
                                        {{
                                            typeof item.component.text === 'function'
                                                ? item.component.text()
                                                : item.component.text
                                        }}
                                    </div>
                                </div>
                            </dx-item>
                        </DxDropDownButton>
                    </dx-item>
                    <dx-item :visible="!!meta.title" location="before" name="titleGrid">
                        <div class="flex gap-2">
                            <h3>{{ `${meta.title}` }}</h3>
                        </div>
                    </dx-item>
                    <dx-item :visible="groupPanelVisible" location="before" name="groupPanel" />
                </dx-toolbar>
                <dx-column
                    v-for="(item, index) in meta?.columns"
                    :key="index"
                    :calculate-cell-value="item.calculateCellValue"
                    :calculate-display-value="item.calculateDisplayValue"
                    :caption="item.caption"
                    :cell-template="item.cellTemplate"
                    :data-field="item.dataField"
                    :data-type="item.dataType || 'string'"
                    :sort-index="item.sortIndex"
                    :sort-order="item.sortOrder"
                    :visible="item.visible"
                    :width="item.width"
                    :show-in-column-chooser="item.showInColumnChooser || true"
                    :format="item.format"
                />
                <template #preferences>
                    <dx-check-box
                        :value="checkboxValue"
                        text="Автоматически сохранять настройки"
                        @value-changed="preferencesCheckChanged"
                    />
                </template>
                <template #titleButton>
                    <div class="flex gap-2">
                        <component :is="meta.icon" />
                        <h3>{{ `${meta.title}` }}</h3>
                    </div>
                </template>
                <template v-for="(_, name) in $slots" #[name]="{ data }">
                    <slot :data="data" :name="name" locate-in-menu="auto"></slot>
                </template>
            </dx-data-grid>
        </div>
        <grid-footer ref="childRef" v-if="showGridFooter" :totalCount="totalCount" />
    </div>
</template>
<script setup>
import { computed, onBeforeMount, onMounted, ref } from 'vue';
import { DxDataGrid, DxColumn, DxToolbar, DxItem } from 'devextreme-vue/data-grid';
import { DxTextBox, DxButton as DxTextBoxButton } from 'devextreme-vue/text-box';
import { DxButton } from 'devextreme-vue';
import DxDropDownButton from 'devextreme-vue/drop-down-button';
import { DxCheckBox } from 'devextreme-vue/check-box';
import gridFooter from '@/components/grid-footer.vue';
import onExporting from '@/utils/export.js';
//  STORING
import { reactive } from 'vue';
import { getDataSource } from '@/service/meta-service';
import { setTabReadyToSaveValue, tabState } from '@/layouts/main-layout/components/navigation-bars-data';
import { mainState, setIsReloadValue } from '@/layouts/main-layout/main-layout-data';

const props = defineProps({
    meta: {
        type: Object,
        default: () => {},
    },
    action: {
        type: Function,
        default: null,
    },
    toolbarConfig: {
        type: Object,
        default: () => {},
    },
    preselectedRows: {
        type: Array,
        default: () => [],
    },
    userSettingsEnabled: {
        type: Boolean,
        default: true,
    },
    showToolbar: {
        type: Boolean,
        default: true,
    },
    showFilterPanel: {
        type: Boolean,
        default: true,
    },
    showGridFooter: {
        type: Boolean,
        default: true,
    },
    showColumnHeaders: {
        type: Boolean,
        default: true,
    },
    args: {
        type: Object,
        default: () => {},
    },
    rowClickCallBack: {
        type: Function,
        default: () => {},
    },
    toolbarUserItems: {
        type: Array,
        default: () => [],
    },
    toRemoveToolbarItemsNames: {
        type: Array,
        default: () => ['choice'],
    },
    preferencesState: {
        type: Boolean,
        default: true,
    },
    autoLoadData: {
        type: Boolean,
        default: true,
    },
});

const refs = ref({
    isChoiceButtonDisabled: true,
});
const groupPanelVisible = ref(false);
const vIsCurrentSettingsUser = ref(true);
let toolbarConfigAfter = [];
const toolbarConfigInitAfter = [
    {
        widget: 'dx-button',
        component: {
            name: 'choice',
            disabledRefName: 'isChoiceButtonDisabled',
            hint: 'Выбрать и закрыть',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-choice-base',
        },
    },
    {
        widget: 'dx-button',
        component: {
            name: 'refresh',
            hint: 'Обновить',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-refresh-base',
        },
    },
    {
        widget: 'searchPanel',
        component: {
            name: 'searchPanel',
        },
    },
    {
        widget: 'dx-drop-down-button',
        component: {
            name: 'export',
            htmlElement: `<svg width="32" height="32" viewBox="0 0 76 74" fill="none" xmlns="http://www.w3.org/2000/svg">
<g clip-path="url(#clip0_1695_4138)">
<path fill-rule="evenodd" clip-rule="evenodd" d="M5 0C2.23858 0 0 2.23858 0 5V69C0 71.7614 2.23858 74 5 74H57C59.7614 74 62 71.7614 62 69V21H44C42.3431 21 41 19.6569 41 18V0H5Z" fill="#286EB6"/>
<path d="M45 1L53 9L61 17H48C46.3431 17 45 15.6569 45 14V1Z" fill="#286EB6"/>
<path d="M38.2068 51.2919L38.2073 51.2951H35.8929C34.0764 51.2951 32 49.9902 32 47.6987V47.6962L32.0092 40.4159C32.0092 40.4154 32.0092 40.4149 32.0092 40.4145V40.4134L34.0092 40.4159L38.2068 51.2919ZM38.2068 51.2919L52.8038 51.2879ZM38.2068 51.2919L52.8038 51.2879ZM52.8038 51.2879V53.4029C52.8038 55.0994 53.9901 56.2437 55.1601 56.7072C56.3556 57.1808 57.8713 57.1203 59.1285 56.2118L72.4615 46.9241L72.4634 46.9228C73.4441 46.238 74.0333 45.1581 73.9986 43.9711C73.9647 42.812 73.3441 41.7944 72.4131 41.1548L59.0607 31.7374L59.0595 31.7365C57.8234 30.866 56.3249 30.8338 55.1618 31.2933C54.0082 31.7491 52.808 32.8766 52.8073 34.5936C52.8073 34.5941 52.8073 34.5946 52.8073 34.5951L52.8057 36.8272L52.8038 51.2879Z" fill="white"/>
<path d="M38.2068 51.2919L38.2073 51.2951H35.8929C34.0764 51.2951 32 49.9902 32 47.6987V47.6962L32.0092 40.4159C32.0092 40.4154 32.0092 40.4149 32.0092 40.4145V40.4134L34.0092 40.4159L38.2068 51.2919ZM38.2068 51.2919L52.8038 51.2879M52.8038 51.2879V53.4029C52.8038 55.0994 53.9901 56.2437 55.1601 56.7072C56.3556 57.1808 57.8713 57.1203 59.1285 56.2118L72.4615 46.9241L72.4634 46.9228C73.4441 46.238 74.0333 45.1581 73.9986 43.9711C73.9647 42.812 73.3441 41.7944 72.4131 41.1548L59.0607 31.7374L59.0595 31.7365C57.8234 30.866 56.3249 30.8338 55.1618 31.2933C54.0082 31.7491 52.808 32.8766 52.8073 34.5936C52.8073 34.5941 52.8073 34.5946 52.8073 34.5951L52.8057 36.8272L52.8038 51.2879Z" stroke="#286EB6" stroke-width="4"/>
<path d="M32 41C32 39.8954 32.8954 39 34 39H55V49H34C32.8954 49 32 48.1046 32 47V41Z" fill="white"/>
</g>
<defs>
<clipPath id="clip0_1695_4138">
<rect width="76" height="74" fill="white"/>
</clipPath>
</defs>
</svg>`,
            items: [
                {
                    widget: 'dx-drop-down-item',
                    component: {
                        htmlElement: 'ai-export-pdf-all-base',
                        name: 'export-pdf-all',
                        text: 'Экспортировать все в pdf',
                    },
                },
                {
                    widget: 'dx-drop-down-item',
                    component: {
                        htmlElement: 'ai-export-pdf-selected-base',
                        name: 'export-pdf-selected',
                        text: 'Экспортировать выбранные строки в pdf',
                    },
                },
                {
                    widget: 'dx-drop-down-item',
                    component: {
                        htmlElement: 'ai-export-excel-all-base',
                        name: 'export-excel-all',
                        text: 'Экспортировать всё в EXCEL',
                    },
                },
                {
                    widget: 'dx-drop-down-item',
                    component: {
                        htmlElement: 'ai-export-excel-selected-base',
                        name: 'export-excel-selected',
                        text: 'Экспортировать выбранные строки в EXCEL',
                    },
                },
            ],
            dropDownOptions: { width: 350 },
        },
    },
    {
        widget: 'dx-drop-down-button',
        component: {
            name: 'settings',
            htmlElement: `<svg width="79" height="79" viewBox="0 0 79 79" fill="none" xmlns="http://www.w3.org/2000/svg">
<path fill-rule="evenodd" clip-rule="evenodd" d="M45.8581 4.08836C42.7691 -0.326979 36.2307 -0.326979 33.1418 4.08836L31.724 6.11496C29.8526 8.78982 26.5073 10.0074 23.3544 9.16124L20.9656 8.52012C15.7612 7.12333 10.7525 11.3261 11.2243 16.694L11.4409 19.1579C11.7267 22.4098 9.94669 25.4929 6.9875 26.8713L4.74549 27.9157C-0.139159 30.191 -1.27454 36.6301 2.53732 40.4388L4.28693 42.187C6.59621 44.4944 7.2144 48.0004 5.83357 50.9584L4.78739 53.1996C2.50808 58.0824 5.77728 63.7448 11.1456 64.2123L13.6096 64.4268C16.8617 64.71 19.5889 66.9984 20.4325 70.152L21.0717 72.5413C22.4642 77.7468 28.6083 79.9831 33.0211 76.8905L35.0466 75.471C37.7199 73.5975 41.28 73.5975 43.9533 75.471L45.9788 76.8905C50.3916 79.9831 56.5357 77.7468 57.9282 72.5413L58.5674 70.152C59.411 66.9984 62.1382 64.71 65.3903 64.4268L67.8543 64.2123C73.2226 63.7448 76.4918 58.0824 74.2125 53.1996L73.1663 50.9584C71.7855 48.0004 72.4037 44.4944 74.713 42.187L76.4626 40.4388C80.2744 36.6301 79.139 30.191 74.2544 27.9157L72.0124 26.8713C69.0532 25.4929 67.2732 22.4098 67.559 19.1579L67.7756 16.694C68.2474 11.3261 63.2387 7.12333 58.0343 8.52012L55.6455 9.16124C52.4926 10.0074 49.1473 8.78982 47.2759 6.11496L45.8581 4.08836ZM39.5 54.9611C47.4867 54.9611 53.9612 48.4866 53.9612 40.4999C53.9612 32.5132 47.4867 26.0386 39.5 26.0386C31.5133 26.0386 25.0388 32.5132 25.0388 40.4999C25.0388 48.4866 31.5133 54.9611 39.5 54.9611Z" fill="#286EB6"/>
</svg>`,
            items: [
                {
                    widget: 'dx-drop-down-item',
                    component: {
                        name: 'group-area',
                        text: () => {
                            return !groupPanelVisible.value
                                ? 'Показать область группировки'
                                : `${String.fromCharCode(10004)}  Показать область группировки`;
                        },
                    },
                },
                {
                    widget: 'dx-drop-down-item',
                    component: {
                        name: 'apply-default-settings',
                        text: () => {
                            return vIsCurrentSettingsUser.value ? `Заводские настройки` : `●  Заводские настройки`;
                        },
                    },
                },
                {
                    widget: 'dx-drop-down-item',
                    component: {
                        name: 'apply-user-settings',
                        text: () => {
                            return !vIsCurrentSettingsUser.value
                                ? `Настройки пользователя`
                                : `●  Настройки пользователя`;
                        },
                    },
                },
                {
                    widget: 'dx-drop-down-item',
                    component: {
                        name: 'clear-user-settings',
                        text: 'Сброс настроек пользователя',
                    },
                },
            ],
            dropDownOptions: { width: 350 },
        },
    },
    {
        widget: 'dx-button',
        component: {
            name: 'show-column-chooser',
            hint: 'Выбор столбцов',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-show-column-chooser-base',
        },
    },
];

const eventController = async e => {
    if (e.item.widget === 'dx-button') {
        if (e.eventName === 'click') {
            await toolbarButtonClick(e);
        }
    }
    if (e.item.widget === 'dx-text-box') {
        if (e.eventName === 'enter-key' || e.eventName === 'key-up') {
            await toolbarTextboxEvent(e);
        }
    }
    if (e.item.widget === 'dx-drop-down-item') {
        if (e.eventName === 'click') {
            await toolbarButtonClick(e);
        }
    }
};

const toolbarButtonClick = async e => {
    switch (e.item.component.name) {
        case 'choice':
            await onChoiceClick(e);
            break;
        case 'refresh':
            await refreshData();
            await emit('refreshClick');
            break;
        case 'export-excel-all':
            onExcelExportAll(e);
            break;
        case 'export-excel-selected':
            onExcelExportSelected(e);
            break;
        case 'export-pdf-all':
            onPdfExportAll(e);
            break;
        case 'export-pdf-selected':
            onPdfExportSelected(e);
            break;
        case 'group-area':
            onGroupAreaClick(e);
            break;
        case 'apply-default-settings':
            applyDefaultSettings(e);
            break;
        case 'apply-user-settings':
            applyUserSettings(e);
            break;
        case 'clear-user-settings':
            clearUserSettings(e);
            break;
        case 'show-column-chooser':
            dxGridRef.value?.instance.showColumnChooser();
            break;
        default:
            break;
    }
    emit('toolbarButtonClick', e);
};

const onChoiceClick = e => {
    const selectedRowKeys = gridInstance.value.getSelectedRowKeys();
    const selectedRows = gridInstance.value.getSelectedRowsData();
    e['data'] = { selectedRowKeys, selectedRows, focusedRowKey: gridInstance.value.option('focusedRowKey') };
};

function onExcelExportAll(e) {
    console.log('onExcelExportAll', e);
}

function onExcelExportSelected(e) {
    console.log('onExcelExportSelected', e);
}

function onPdfExportAll(e) {
    console.log('onPdfExportAll', e);
}

function onPdfExportSelected(e) {
    console.log('onPdfExportSelected', e);
}

const onGroupAreaClick = () => {
    groupPanelVisible.value = !groupPanelVisible.value;
};

const applyDefaultSettings = () => {
    vIsCurrentSettingsUser.value = false;
};
const applyUserSettings = () => {
    vIsCurrentSettingsUser.value = true;
};
const clearUserSettings = () => {
    emit('userSettingsReset');
};
const toolbarTextboxEvent = async e => {
    switch (e.eventName) {
        case 'enter-key':
            switch (e.item.component.name) {
                case 'find':
                    await onEnterKeyFilter(e);
                    break;
                default:
                    break;
            }
            break;
        case 'key-up':
            switch (e.item.component.name) {
                case 'find':
                    await onFilterKeyup(e);
                    break;
                default:
                    break;
            }
            break;
        default:
            break;
    }
    emit('toolbarTextboxEvent', e);
};
const onEnterKeyFilter = e => {
    console.log('onEnterKeyFilter($event): ', e);
};
const onFilterKeyup = async e => {
    console.log('onFilter1Keyup($event): ', e.event.currentTarget.value);
};

const state = reactive({
    dataSource: [],
    prefState: true,
});

const onEditorPreparing = event => {
    if (event.parentType === 'searchPanel' || event.parentType === 'filterRow') {
        event.updateValueTimeout = 3000;
    }
};
const setDataSource = async (cache, apiArgs, data) => {
    if (data) {
        state.dataSource = data;
        return;
    }

    if (cache) {
        state.dataSource = cache;
        return;
    }

    const finalArgs = apiArgs ? apiArgs : args.value;
    const res = await getDataSource(props.meta.api.select, finalArgs);
    state.dataSource = res.result ? res.data : [];
    return res;
};
//  STORING

const totalCount = ref(props.preselectedRows?.length);
const dxGridRef = ref();
const gridInstance = ref();
const childRef = ref();
const buttonsInstances = {};
const toolbarItems = ref([]);
const checkboxValue = computed(() => true); //to do: settings saving connecting
const exportFormats = computed(() => (props.meta?.exportFormats ? props.meta.exportFormats : ['xlsx', 'pdf']));
const userSettingsData = ref({});
const preselectedRows = computed(() => {
    const filteredKeys = props.preselectedRows;
    return filteredKeys.sort((a, b) => a - b);
});
let _dinamicArgs = ref({});
const args = computed(() => (props.args === undefined ? {} : props.args));
const contentReadyCounter = ref(0);
const displayList = computed(() => state.dataSource ?? []);
const lazyFocusKeyExpr = ref();

onBeforeMount(() => {
    // toolbar
    for (const item of toolbarConfigInitAfter) {
        if (!props.toRemoveToolbarItemsNames.includes(item.component.name)) {
            toolbarConfigAfter.push({ ...item });
        }
    }
    const compare = (a, b) => {
        if (a.index < b.index) {
            return -1;
        }
        if (a.index > b.index) {
            return 1;
        }
        return 0;
    };
    const toolbarUserItems = [...props.toolbarUserItems].sort(compare);

    for (const item of toolbarUserItems) {
        if (item.component.valueRefName) {
            refs.value[item.component.valueRefName.name] = item.component.valueRefName.value;
        }
        toolbarConfigAfter.splice(item.index, 0, item);
    }
});

const setCacheOrApiDataSource = async () => {
    if (tabState[mainState.routePath] && tabState[mainState.routePath]?.data) {
        //
    } else {
        tabState[mainState.routePath] ??= {};
    }
    // if (tabState[mainState.routePath] && tabState[mainState.routePath]?.data) {
    //     //
    // } else {
    //     tabState[mainState.routePath] ??= {};
    // }
};

onMounted(async () => {
    // load data
    const vAutoLoadData = computed(() => {
        if (props.meta?.autoLoadData !== undefined) {
            return props.meta?.autoLoadData;
        }
        if (props.autoLoadData !== undefined) {
            return props.autoLoadData;
        }
        return true;
    });
    gridInstance.value = dxGridRef.value.instance;
    if (vAutoLoadData.value) {
        await refreshData();
        if (mainState.isReload || props.meta.hasntRoute) {
            setCacheOrApiDataSource();
            setIsReloadValue(false);
        }
    }
    if (props.action)
        props.action({
            instance: gridInstance.value,
            meta: props.meta,
            setUserDataChanges,
            setFocusdRow,
            componentRef: dxGridRef.value,
        });
});

async function refreshData(dinamicArgs) {
    if (dinamicArgs !== undefined) {
        _dinamicArgs.value = dinamicArgs;
    }
    const _args = { ...args.value, ..._dinamicArgs.value };
    await setDataSource(false, _args);
}

const emit = defineEmits([
    'selectClick',
    'saveClick',
    'userSettingsChanged',
    'userSettingsReset',
    'rowClick',
    'toolbarButtonClick',
    'refreshData',
]);

const setUserDataChanges = (userData, isReady) => {
    gridInstance.value.option('editing.changes', userData);
    setTabReadyToSaveValue(false);
    if (isReady) {
        setTabReadyToSaveValue(true);
    }
};

const saveState = state => {
    const { searchText, filterValue, columns } = state;

    userSettingsData.value = { searchText, filterValue, columns, preferencesState: props.preferencesState };

    if (props.preferencesState && props.userSettingsEnabled) {
        emit('userSettingsChanged', userSettingsData.value);
    }
};

const setFocusdRow = keyExprValue => {
    if (displayList.value.length) {
        _setFocusedRow(keyExprValue);
    } else {
        lazyFocusKeyExpr.value = keyExprValue;
    }
};

const _setFocusedRow = keyExprValue => {
    const neededRowIndex = gridInstance.value.getRowIndexByKey(keyExprValue);
    gridInstance.value.option('focusedRowIndex', neededRowIndex);
};

const onRowClick = event => {
    emit('rowClick', event);
    props.rowClickCallBack({ row: event.data, keyExpr: props.meta.keyExpr });
};

const onRoFocusChanged = async ({ rowIndex, row, component }) => {
    if (rowIndex !== -1 && rowIndex !== 0) {
        emit('gridRoFocusChanged', row, props.meta.viewId, rowIndex, component);
    }
};

const selectionChanged = event => {
    const { selectedRowKeys } = event;
    const { selectButtonInstance } = buttonsInstances;
    childRef.value?.selectRows(selectedRowKeys.length, totalCount);

    if (selectedRowKeys.length > 0) {
        if (props.meta.selectButtonMode === 'single') {
            if (selectedRowKeys.length === 1) {
                selectButtonInstance?.option('disabled', false);
                refs.value['isChoiceButtonDisabled'] = false;
            } else {
                selectButtonInstance?.option('disabled', true);
                refs.value['isChoiceButtonDisabled'] = true;
            }
        } else {
            selectButtonInstance?.option('disabled', false);
            refs.value['isChoiceButtonDisabled'] = false;
        }
    } else {
        selectButtonInstance?.option('disabled', true);
        refs.value['isChoiceButtonDisabled'] = true;
    }
};

const onContentReady = () => {
    totalCount.value = gridInstance.value?.totalCount();

    if (contentReadyCounter.value < 5 && preselectedRows.value.length > 0) {
        const index = gridInstance.value.getRowIndexByKey(preselectedRows.value[0]);
        if (index !== -1) {
            const element = gridInstance.value.getRowElement(index)[0];
            gridInstance.value.getScrollable()?.scrollToElement(element);
        }
    }

    contentReadyCounter.value += 1;
};

const onSaved = () => {
    totalCount.value = gridInstance.value.totalCount();
};

const preferencesCheckChanged = ({ value }) => {
    const newItems = [];
    const { preferencesButtonInstance } = buttonsInstances;

    if (value) {
        preferencesButtonInstance?.option('items').map(item => {
            if (item.id === 2) item.disabled = true;
            newItems.push(item);
        });
        userSettingsData.value = { ...userSettingsData.value, preferencesState: true };
    } else {
        preferencesButtonInstance?.option('items').map(item => {
            if (item.id === 2) item.disabled = false;
            newItems.push(item);
        });
        userSettingsData.value = { ...userSettingsData.value, preferencesState: false };
    }

    preferencesButtonInstance?.option('items', newItems);
    if (props.userSettingsEnabled) emit('userSettingsChanged', userSettingsData.value); //to do: settings connecting
};

defineExpose({
    gridInstance,
    setDataSource,
    expandAll: () => dxGridRef.value.expandAll(),
    collapseAll: () => dxGridRef.value.collapseAll(),
    getSelectedRowKeys: () => dxGridRef.value.getSelectedRowKeys(),
    getGridDataSource: () => {
        return state.dataSource;
    },
    refreshSaveButtonDisable: () => dxGridRef.value.refreshSaveButtonDisable(),
    refreshData,
    setCacheOrApiDataSource,
});
</script>

<style lang="scss" scoped>
.grid-border {
    border: 1px solid #e5e7eb;
}
</style>
